import React, { useContext, useRef, useState } from 'react'
import { useDispatch } from 'react-redux';
import { useOutsideClickHandler } from '../../../../../Hooks/useOutsideClick';
import { closeModal } from '../../../../../Features/inventoryManagementSlice';
import Cross from '../../../../../Assets/Modals/closeIcon.svg';
import AddBlue from '../../../../../Assets/Modals/AddBlue.svg';
import ImageAdd from '../../../../../Assets/Add image.svg';
import { loadImage, toCamelCase } from '../../../../../lib/utils/utils';
import { showToast } from '../../../../../Components/Toaster/Toaster';
import { v4 as uuidv4 } from 'uuid';
import VariantImagePreview from './VariantImagePreview';
import { InventoryAddProductFormContext } from '../../../../../Context/InventoryAddProductFormContext';

function ColorVariant() {
    const dispatch = useDispatch();

    const containerRef = useRef(null);

    useOutsideClickHandler(containerRef, () => {
        dispatch(closeModal());
    });

    const { colorVariants, setColorVariants } = useContext(InventoryAddProductFormContext);

    const handleCloseModal = () => {
        dispatch(closeModal());
    }

    const defaultOptionList = [
        {
            id: 'optionValue1',
            value: '',
            images: []
        }
    ]

    const [optionList, setOptionList] = useState(defaultOptionList);

    const handleChange = (e) => {
        const { name, value } = e.target;
        setOptionList(prevOptions => 
            prevOptions.map(option => 
                option.id === name ? { ...option, value } : option
            )
        );
    }

    const handleImageSelection = async (e) => {
        const optionId = e.target.id;
        const files = Array.from(e.target.files);

        let images = optionList.find(item => item.id === optionId.split('-')[0]).images;

        if (files.length > 0) {
            if((files.length + images.length) > 5) {
                showToast('Maximum 5 images are allowed.', 'warning');
            }
            else {
                const selectedImages = await Promise.all(files.map(async (file) => {
                    const img = await loadImage(file);

                    return {
                        id: uuidv4(),
                        url: img.src,
                        file: file
                    };
                }));

                setOptionList(prevOptions => 
                    prevOptions.map(option => 
                        `${option.id}-images` === optionId ? { ...option, images: [ ...option.images, ...selectedImages ] } : option
                    )
                );
            }
        }
        e.target.value = null;
    };

    const replaceImage = (newImage) => {
        setOptionList(prevOptions => 
            prevOptions.map(option => 
                ({
                    ...option,
                    images: option.images.map(image => 
                        image.id === newImage.id ? newImage : image
                    )
                })
            )
        );
    }

    const deleteImage = (imageId) => {
        setOptionList(prevOptions => 
            prevOptions.map(option => 
                ({
                    ...option,
                    images: option.images.filter(image => 
                        image.id !== imageId
                    )
                })
            )
        );
    }

    // helper: reindex IDs after add/remove
    const reindexOptions = (options) => {
        return options.map((option, index) => ({
            ...option,
            id: `optionValue${index + 1}`,
        }));
    };

    // add new variant
    const handleAddOption = () => {
        setOptionList((prevOptions) =>
            reindexOptions([
                ...prevOptions,
                { id: "", value: "", images: [] }, // id will be reset by reindexOptions
            ])
        );
    };

    // remove a variant
    const handleRemoveOption = (optionId) => {
        setOptionList((prevOptions) =>
            reindexOptions(prevOptions.filter((option) => option.id !== optionId))
        );
    };

    const handleSave = () => {
        let variantsArray = [];
        let allValid = true;

        for (const option of optionList) {
            if(!option.value.trim()) {
                showToast('Please add a color name.', 'warning');
                allValid = false;
            }
            else if(option.images.length === 0) {
                showToast(`Please add images for ${option.value} color variant.`, 'warning');
                allValid = false;
            }
            else {
                let colorObject = {
                    color: option.value,
                    id: toCamelCase(option.value),
                    variantName: '',
                    images: option.images,
                    primary: false
                };

                variantsArray.push(colorObject);
            }
        }

        if (allValid && variantsArray.length > 0) {
            setColorVariants([ ...colorVariants, ...variantsArray ]);
            dispatch(closeModal());
        }
    };

    return (
        <div ref={containerRef} className='w-[45%] h-fit p-6 pt-[18px] flex flex-col gap-6 rounded-xl bg-[#FFFFFF] shadow-[0px_0px_12px_0px_#0000003D]'>
            <div className="flex flex-col gap-4">
                <div className="flex items-center justify-between">
                    <p className='font-semibold text-2xl text-[#121212]'>Add Color</p>

                    <button onClick={handleCloseModal} className="outline-none w-11 h-11 flex items-center justify-center">
                        <img src={Cross} alt="" />
                    </button>
                </div>
                
                <div className="flex flex-col gap-3 max-h-96 overflow-y-scroll">
                    {optionList.map(option => {
                        const uniqueId = `${option.id}-images`;

                        return (
                            <div key={option.id} className="flex flex-col gap-3 border-b border-[#EAEAEA]">
                                <div className="flex flex-col gap-2">
                                    <div className="flex items-center justify-between">
                                        <label
                                            className="select-none cursor-pointer w-fit font-medium text-base text-[#121212]"
                                            htmlFor={option.id}
                                        >
                                            Input value
                                            <span className="text-[#DC2626]">*</span>
                                        </label>

                                        {/* errorText optional */}
                                        {/* <p className="text-[14px] leading-5 text-[#DC2626] select-none">{errorText}</p> */}
                                        <button onClick={() => handleRemoveOption(option.id)} className='text-[14px] leading-5 text-[#DC2626] select-none'>Remove</button>
                                    </div>

                                    <input
                                        className="outline-none px-3 py-4 bg-[#F5F5F5] border border-[#EAEAEA] rounded-[9px] font-normal text-base text-[#121212] focus:border-primaryColor"
                                        type="text"
                                        id={option.id}
                                        name={option.id}
                                        placeholder="enter color"
                                        value={option.value}
                                        onChange={handleChange}
                                    />
                                </div>

                                <div className="flex flex-wrap gap-3">
                                    {
                                        option.images.length < 5 && 
                                        <label htmlFor={uniqueId} className='cursor-pointer w-[88px] h-[88px] flex items-center justify-center border-[3px] border-[#EAEAEA] rounded-xl border-dashed'>
                                            <img src={ImageAdd} alt="" />
                                        </label>
                                    }

                                    {option.images.map(image => {
                                        return (
                                            <VariantImagePreview 
                                                key={image.id} 
                                                image={image} 
                                                handleReplace={replaceImage} 
                                                handleDelete={deleteImage} 
                                            />
                                        );
                                    })}
                                </div>

                                <input 
                                    className='hidden' 
                                    type="file" 
                                    accept="image/*" 
                                    name={uniqueId} 
                                    id={uniqueId} 
                                    multiple 
                                    onChange={handleImageSelection} 
                                />

                                <p className="text-[14px] leading-[22px] text-[#666666]">*Max 5 images</p>

                                <div className="w-full h-[1px] bg-[#EAEAEA]"></div>
                            </div>
                        )
                    })}
                </div>

                <button onClick={handleAddOption} className='w-fit flex items-center'>
                    <div className="w-6 h-6 flex items-center justify-center">
                        <img src={AddBlue} alt="" />
                    </div>

                    <p className='font-medium text-base text-[#1055EB]'>Add option</p>
                </button>
            </div>

            <button onClick={handleSave} className='px-4 py-2 rounded bg-primaryColor hover:bg-primaryAccent font-semibold text-base text-buttonText'>Save</button>
        </div>
    );
}

export default ColorVariant;
